import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PortalShell } from "@/components/AppHeader";
import { api, errMsg, fmtDate, money } from "@/lib/api";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function DriverSchedule() {
  const [blocks, setBlocks] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [busy, setBusy] = useState(false);
  const apply = (d) => { setBlocks(d.blocks); setRentals(d.upcoming_rentals); };
  useEffect(() => { api.get("/driver/schedule").then(({ data }) => apply(data)); }, []);
  const set = (i, k, v) => setBlocks((b) => b.map((x, j) => (j === i ? { ...x, [k]: k === "day" ? Number(v) : v } : x)));
  const save = async () => {
    setBusy(true);
    try { apply((await api.put("/driver/schedule", { blocks })).data); toast.success("Schedule saved"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <PortalShell>
      <p className="lux-eyebrow">Weekly availability</p>
      <h1 className="mt-2 text-3xl font-semibold text-amber-50 sm:text-4xl">Schedule</h1>
      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <div className="lux-card space-y-3 p-5 sm:p-6" data-testid="schedule-blocks">
          <p className="lux-eyebrow">Availability blocks</p>
          {blocks.length === 0 && <p className="text-sm text-zinc-500">No blocks yet — add the times you usually drive.</p>}
          {blocks.map((b, i) => (
            <div key={i} data-testid={`schedule-block-${i}`} className="grid grid-cols-[1fr_1fr_1fr_auto] items-center gap-2">
              <select data-testid={`schedule-day-${i}`} className="lux-input !px-2" value={b.day} onChange={(e) => set(i, "day", e.target.value)}>{DAYS.map((d, k) => <option key={d} value={k}>{d}</option>)}</select>
              <input type="time" data-testid={`schedule-start-${i}`} className="lux-input !px-2" value={b.start} onChange={(e) => set(i, "start", e.target.value)} />
              <input type="time" data-testid={`schedule-end-${i}`} className="lux-input !px-2" value={b.end} onChange={(e) => set(i, "end", e.target.value)} />
              <button data-testid={`schedule-remove-${i}`} onClick={() => setBlocks(blocks.filter((_, j) => j !== i))} className="p-2 text-zinc-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <div className="flex flex-wrap gap-3 pt-2">
            <button data-testid="schedule-add-button" onClick={() => setBlocks([...blocks, { day: 0, start: "08:00", end: "16:00" }])} className="btn-outline-gold !py-2.5"><Plus className="h-4 w-4" /> Add block</button>
            <button data-testid="schedule-save-button" onClick={save} disabled={busy} className="btn-gold !py-2.5">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save schedule</button>
          </div>
        </div>
        <div className="lux-card p-5 sm:p-6" data-testid="schedule-rentals">
          <p className="lux-eyebrow">Upcoming accepted rentals</p>
          {rentals.length === 0 && <p className="mt-3 text-sm text-zinc-500">No upcoming rentals.</p>}
          {rentals.map((r) => (
            <Link key={r.id} to={`/trips/${r.id}`} data-testid={`schedule-rental-${r.id}`} className="block border-b border-white/5 py-3 text-sm last:border-0 hover:text-amber-200">
              <p className="text-amber-100">{fmtDate(r.rental?.start_at)} · {r.rental?.hours}h</p>
              <p className="text-zinc-400">{r.pickup.address} · {money(r.fare.total)}</p>
            </Link>
          ))}
        </div>
      </div>
    </PortalShell>
  );
}
